import { lodestarColors, ursaColors } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { RequiredThemeKeys, themed } from "@smartsheet/sds-theme-internal";

/**
 * These colors names look a LOT like our color tokens. But they're not
 * necessarily those colors and aren't required to be.
 *
 * This uses the same color naming scheme as the colors but that's just cause
 * it's a nice naming scheme.
 *
 * For example, blueDark10 and blueDark20 are actually mapped to visBlueDark20
 * and visBlueDark30, respectively, to ensure proper 4.5:1 contrast.
 */
export const colorTheme = themed({
    // These variants are only used in Ursa.
    aqua: {
        textColor: color("color--text--spectrum-aqua--strong"),
        backgroundColor: color("color--background--spectrum-aqua--weak"),
        backgroundHoverColor: color("color--background--spectrum-aqua--weak"),
        borderColor: color("color--border--spectrum-aqua--default"),
    },
    azure: {
        textColor: color("color--text--spectrum-azure--strong"),
        backgroundColor: color("color--background--spectrum-azure--weak"),
        backgroundHoverColor: color("color--background--spectrum-azure--weak"),
        borderColor: color("color--border--spectrum-azure--default"),
    },
    gold: {
        textColor: color("color--text--spectrum-gold--strong"),
        backgroundColor: color("color--background--spectrum-gold--weak"),
        backgroundHoverColor: color("color--background--spectrum-gold--weak"),
        borderColor: color("color--background--spectrum-gold--default"),
    },
    indigo: {
        textColor: color("color--text--spectrum-indigo--strong"),
        backgroundColor: color("color--background--spectrum-indigo--weak"),
        backgroundHoverColor: color("color--background--spectrum-indigo--weak"),
        borderColor: color("color--border--spectrum-indigo--default"),
    },
    mint: {
        textColor: color("color--text--spectrum-mint--strong"),
        backgroundColor: color("color--background--spectrum-mint--weak"),
        backgroundHoverColor: color("color--background--spectrum-mint--weak"),
        borderColor: color("color--border--spectrum-mint--default"),
    },
    orchid: {
        textColor: color("color--text--spectrum-orchid--strong"),
        backgroundColor: color("color--background--spectrum-orchid--weak"),
        backgroundHoverColor: color("color--background--spectrum-orchid--weak"),
        borderColor: color("color--border--spectrum-orchid--default"),
    },
    pink: {
        textColor: color("color--text--spectrum-pink--strong"),
        backgroundColor: color("color--background--spectrum-pink--weak"),
        backgroundHoverColor: color("color--background--spectrum-pink--weak"),
        borderColor: color("color--border--spectrum-pink--default"),
    },
    rose: {
        textColor: color("color--text--spectrum-rose--strong"),
        backgroundColor: color("color--background--spectrum-rose--weak"),
        backgroundHoverColor: color("color--background--spectrum-rose--weak"),
        borderColor: color("color--border--spectrum-rose--default"),
    },
    violet: {
        textColor: color("color--text--spectrum-violet--strong"),
        backgroundColor: color("color--background--spectrum-violet--weak"),
        backgroundHoverColor: color("color--background--spectrum-violet--weak"),
        borderColor: color("color--border--spectrum-violet--default"),
    },

    // These color variants are used in Lodestar AND Ursa and need to swap values between.
    yellow: {
        textColor: {
            "lodestar-light": color("color--text--neutral--strong"),
            "ursa-light": color("color--text--spectrum-yellow--strong"),
        },
        backgroundColor: {
            "lodestar-light": color("color--background--spectrum-yellow--default"),
            "ursa-light": color("color--background--spectrum-yellow--weak"),
        },
        backgroundHoverColor: {
            "lodestar-light": color("color--background--spectrum-yellow--default"),
            "ursa-light": color("color--background--spectrum-yellow--weak"),
        },
        borderColor: {
            "lodestar-light": color("color--background--spectrum-yellow--default"),
            "ursa-light": color("color--border--spectrum-yellow--default"),
        },
    },
    blue: {
        textColor: {
            "lodestar-light": color("color--text--neutral--strong"),
            "ursa-light": color("color--text--spectrum-blue--strong"),
        },
        backgroundColor: color("color--background--spectrum-blue--weak"),
        backgroundHoverColor: color("color--background--spectrum-blue--weak"),
        borderColor: {
            "lodestar-light": color("color--background--spectrum-blue--default"),
            "ursa-light": color("color--border--spectrum-blue--default"),
        },
    },
    green: {
        textColor: {
            "lodestar-light": color("color--text--neutral--strong"),
            "ursa-light": color("color--text--spectrum-green--strong"),
        },
        backgroundColor: {
            "lodestar-light": color("color--background--spectrum-green--default"),
            "ursa-light": color("color--background--spectrum-green--weak"),
        },
        backgroundHoverColor: {
            "lodestar-light": color("color--background--spectrum-green--default"),
            "ursa-light": color("color--background--spectrum-green--weak"),
        },
        borderColor: {
            "lodestar-light": color("color--background--spectrum-green--default"),
            "ursa-light": color("color--border--spectrum-green--default"),
        },
    },
    neutral: {
        textColor: {
            "lodestar-light": color("color--text--neutral--strong"),
            "ursa-light": color("color--text--spectrum-neutral--strong"),
        },
        backgroundColor: {
            "lodestar-light": color("color--background--spectrum-neutral--stronger"),
            "ursa-light": color("color--background--neutral--strong"),
        },
        backgroundHoverColor: {
            "lodestar-light": color("color--background--spectrum-neutral--stronger"),
            "ursa-light": color("color--background--neutral--strong"),
        },
        borderColor: color("color--border--spectrum-neutral--default"),
    },
    orange: {
        textColor: {
            "lodestar-light": color("color--text--neutral--strong"),
            "ursa-light": color("color--text--spectrum-orange--strong"),
        },
        backgroundColor: {
            "lodestar-light": color("color--background--spectrum-orange--default"),
            "ursa-light": color("color--background--spectrum-orange--weak"),
        },
        backgroundHoverColor: {
            "lodestar-light": color("color--background--spectrum-orange--default"),
            "ursa-light": color("color--background--spectrum-orange--weak"),
        },
        borderColor: {
            "lodestar-light": color("color--background--spectrum-orange--default"),
            "ursa-light": color("color--border--spectrum-orange--default"),
        },
    },
    red: {
        textColor: {
            "lodestar-light": color("color--text--neutral--strong"),
            "ursa-light": color("color--text--spectrum-red--strong"),
        },
        backgroundColor: {
            "lodestar-light": color("color--background--spectrum-red--default"),
            "ursa-light": color("color--background--spectrum-red--weak"),
        },
        backgroundHoverColor: {
            "lodestar-light": color("color--background--spectrum-red--default"),
            "ursa-light": color("color--background--spectrum-red--weak"),
        },
        borderColor: {
            "lodestar-light": color("color--background--spectrum-red--default"),
            "ursa-light": color("color--border--spectrum-red--default"),
        },
    },
    teal: {
        textColor: {
            "lodestar-light": color("color--text--neutral--strong"),
            "ursa-light": color("color--text--spectrum-teal--strong"),
        },
        backgroundColor: color("color--background--spectrum-teal--weak"),
        backgroundHoverColor: color("color--background--spectrum-teal--weak"),
        borderColor: {
            "lodestar-light": color("color--background--spectrum-teal--default"),
            "ursa-light": color("color--border--spectrum-teal--default"),
        },
    },

    // These color variants are only used in Lodestar and will not have tokens associated with them.
    berryLight10: {
        textColor: lodestarColors.neutralDark40,
        backgroundColor: lodestarColors.visBerryLight10,
        backgroundHoverColor: lodestarColors.visBerryLight10,
        borderColor: lodestarColors.visBerryLight10,
    },
    berry: {
        textColor: lodestarColors.neutralDark40,
        backgroundColor: lodestarColors.visBerry,
        backgroundHoverColor: lodestarColors.visBerry,
        borderColor: lodestarColors.visBerry,
    },
    berryDark10: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visBerryDark10,
        backgroundHoverColor: lodestarColors.visBerryDark10,
        borderColor: lodestarColors.visBerryDark10,
    },
    berryDark20: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visBerryDark20,
        backgroundHoverColor: lodestarColors.visBerryDark20,
        borderColor: lodestarColors.visBerryDark20,
    },
    blueLight10: {
        textColor: lodestarColors.neutralDark40,
        backgroundColor: lodestarColors.visBlueLight10,
        backgroundHoverColor: lodestarColors.visBlueLight10,
        borderColor: lodestarColors.visBlueLight10,
    },
    blueDark10: {
        textColor: lodestarColors.neutralLight40,
        // minor deviations here between naming schema and selected color
        // to ensure an accessible contrast ratio.
        backgroundColor: lodestarColors.visBlueDark20,
        backgroundHoverColor: lodestarColors.visBlueDark20,
        borderColor: lodestarColors.visBlueDark20,
    },
    blueDark20: {
        textColor: lodestarColors.neutralLight40,
        // minor deviations here between naming schema and selected color
        // to ensure an accessible contrast ratio.
        backgroundColor: lodestarColors.visBlueDark30,
        backgroundHoverColor: lodestarColors.visBlueDark30,
        borderColor: lodestarColors.visBlueDark30,
    },
    greenLight10: {
        textColor: lodestarColors.neutralDark40,
        backgroundColor: lodestarColors.visGreenLight10,
        backgroundHoverColor: lodestarColors.visGreenLight10,
        borderColor: lodestarColors.visGreenLight10,
    },
    greenDark10: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visGreenDark10,
        backgroundHoverColor: lodestarColors.visGreenDark10,
        borderColor: lodestarColors.visGreenDark10,
    },
    greenDark20: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visGreenDark20,
        backgroundHoverColor: lodestarColors.visGreenDark20,
        borderColor: lodestarColors.visGreenDark20,
    },
    orangeLight10: {
        textColor: lodestarColors.neutralDark40,
        backgroundColor: lodestarColors.visOrangeLight10,
        backgroundHoverColor: lodestarColors.visOrangeLight10,
        borderColor: lodestarColors.visOrangeLight10,
    },
    orangeDark10: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visOrangeDark10,
        backgroundHoverColor: lodestarColors.visOrangeDark10,
        borderColor: lodestarColors.visOrangeDark10,
    },
    orangeDark20: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visOrangeDark20,
        backgroundHoverColor: lodestarColors.visOrangeDark20,
        borderColor: lodestarColors.visOrangeDark20,
    },
    purpleLight10: {
        textColor: lodestarColors.neutralDark40,
        backgroundColor: lodestarColors.visPurpleLight10,
        backgroundHoverColor: lodestarColors.visPurpleLight10,
        borderColor: lodestarColors.visPurpleLight10,
    },
    purple: {
        textColor: lodestarColors.neutralDark40,
        backgroundColor: lodestarColors.visPurple,
        backgroundHoverColor: lodestarColors.visPurple,
        borderColor: lodestarColors.visPurple,
    },
    purpleDark10: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visPurpleDark10,
        backgroundHoverColor: lodestarColors.visPurpleDark10,
        borderColor: lodestarColors.visPurpleDark10,
    },
    purpleDark20: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visPurpleDark20,
        backgroundHoverColor: lodestarColors.visPurpleDark20,
        borderColor: lodestarColors.visPurpleDark20,
    },
    redLight10: {
        textColor: lodestarColors.neutralDark40,
        backgroundColor: lodestarColors.visRedLight10,
        backgroundHoverColor: lodestarColors.visRedLight10,
        borderColor: lodestarColors.visRedLight10,
    },
    redDark10: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visRedDark10,
        backgroundHoverColor: lodestarColors.visRedDark10,
        borderColor: lodestarColors.visRedDark10,
    },
    redDark20: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visRedDark20,
        backgroundHoverColor: lodestarColors.visRedDark20,
        borderColor: lodestarColors.visRedDark20,
    },
    tealLight10: {
        textColor: lodestarColors.neutralDark40,
        backgroundColor: lodestarColors.visTealLight10,
        backgroundHoverColor: lodestarColors.visTealLight10,
        borderColor: lodestarColors.visTealLight10,
    },
    tealDark10: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visTealDark10,
        backgroundHoverColor: lodestarColors.visTealDark10,
        borderColor: lodestarColors.visTealDark10,
    },
    tealDark20: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visTealDark20,
        backgroundHoverColor: lodestarColors.visTealDark20,
        borderColor: lodestarColors.visTealDark20,
    },
    yellowLight10: {
        textColor: lodestarColors.neutralDark40,
        backgroundColor: lodestarColors.visYellowLight10,
        backgroundHoverColor: lodestarColors.visYellowLight10,
        borderColor: lodestarColors.visYellowLight10,
    },
    yellowDark10: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visYellowDark10,
        backgroundHoverColor: lodestarColors.visYellowDark10,
        borderColor: lodestarColors.visYellowDark10,
    },
    yellowDark20: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visYellowDark20,
        backgroundHoverColor: lodestarColors.visYellowDark20,
        borderColor: lodestarColors.visYellowDark20,
    },
});

export type ThemeColorNames = keyof typeof colorTheme;

export const getColors = (color: keyof typeof colorTheme, themeMode: RequiredThemeKeys) => {
    return {
        textColor: colorTheme[color].textColor(themeMode),
        backgroundColor: colorTheme[color].backgroundColor(themeMode),
        backgroundHoverColor: colorTheme[color].backgroundHoverColor(themeMode),
        borderColor: colorTheme[color].borderColor(themeMode),
    };
};

export const avatarTheme = themed({
    focusRingGap: {
        "lodestar-light": 2,
        "ursa-light": 1,
    },
    weight: {
        "lodestar-light": 700,
        "ursa-light": 600,
    },
    badge: {
        backgroundColor: {
            error: ursaColors.red75,
            default: ursaColors.slate60,
            success: ursaColors.mint50,
            warning: ursaColors.gold75,
        },
        iconColor: {
            error: ursaColors.red800,
            default: ursaColors.neutral800,
            success: ursaColors.green800,
            warning: ursaColors.gold900,
        },
    },
} as const);

interface AvatarBackgroundProps {
    backgroundColor: string;
    backgroundImage: string | undefined;
    hasError: boolean;
}

export const getBackground = (props: AvatarBackgroundProps) => {
    if (props.backgroundImage && !props.hasError) {
        return `url(${props.backgroundImage}) center center no-repeat`;
    }
    return props.backgroundColor;
};

interface AvatarBackgroundHoverProps {
    backgroundColor: string;
    hasBackgroundImage: boolean;
    hasError: boolean;
}
export const getBackgroundHoverColor = (props: AvatarBackgroundHoverProps) => {
    if (props.hasBackgroundImage && !props.hasError) {
        // Returning undefined means the CSS custom property won't be set.
        return undefined;
    }
    return props.backgroundColor;
};
